import React, { useMemo } from 'react';

interface TextRun {
  text: string;
  bold: boolean;
}

interface Change {
  changed_en_runs?: TextRun[];
  changed_runs?: TextRun[];
  change_time: number;
  user_id: number;
  username: string;
}

interface EnChangeHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  changeHistory: any[];
  rowSid: string;
  isTurkish?: boolean; // Flag to indicate if this is Turkish text
}

const EnChangeHistoryModal: React.FC<EnChangeHistoryModalProps> = ({
  isOpen,
  onClose,
  changeHistory,
  rowSid,
  isTurkish = false
}) => {

  // Find changes for this specific row
  const rowChanges = useMemo(() => {
    const rowData = changeHistory.find(item => item.sid === rowSid);
    return rowData?.changes || [];
  }, [changeHistory, rowSid]);

  // Get original runs (en_runs for English, runs for Turkish)
  const originalRuns = useMemo(() => {
    const rowData = changeHistory.find(item => item.sid === rowSid);
    const runsKey = isTurkish ? 'runs' : 'en_runs';
    if (rowData?.[runsKey]) {
      try {
        return JSON.parse(rowData[runsKey]);
      } catch (e) {
        return null;
      }
    }
    return null;
  }, [changeHistory, rowSid, isTurkish]);

  // Format timestamp to readable date
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Render text runs with bold support
  const renderTextRuns = (runs: TextRun[]) => {
    if (!runs || runs.length === 0) return null;
    
    return (
      <span>
        {runs.map((run: TextRun, idx: number) => (
          <span key={idx} className={run.bold ? 'font-bold' : ''}>
            {run.text}
          </span>
        ))}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 flex items-center justify-between px-8 py-6 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Değişiklik Geçmişi
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 px-8 py-6">{/* Content */}
        {rowChanges.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              Bu satır için henüz değişiklik kaydı bulunmuyor.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Timeline */}
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-6 top-8 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-purple-500"></div>
              
              {/* Changes */}
              <div className="space-y-6">
                {rowChanges.map((change: Change, index: number) => (
                  <div key={index} className="relative pl-16">
                    {/* Timeline dot */}
                    <div className="absolute left-3 top-2 w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full border-4 border-white dark:border-gray-800 shadow-lg flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{rowChanges.length - index}</span>
                    </div>
                    
                    {/* Change card */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 border-blue-200 dark:border-gray-600 border rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300">
                      {/* Change Badge */}
                      <div className="flex items-center mb-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-600 dark:bg-blue-500 text-white shadow-sm">
                          <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                          </svg>
                          Değişiklik #{rowChanges.length - index}
                        </span>
                      </div>
                      {/* Header with date and user */}
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 dark:border-gray-600">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              {change.username}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(change.change_time)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            index === 0
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {index === 0 ? 'Güncel' : 'Geçmiş'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Changed text */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                              {isTurkish ? 'Türkçe Metin' : 'İngilizce Metin'}
                            </p>
                            <p className="text-base text-gray-900 dark:text-white leading-relaxed">
                              {renderTextRuns((isTurkish ? change.changed_runs : change.changed_en_runs) || [])}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Original Version - If available */}
            {originalRuns && originalRuns.length > 0 && (
              <div className="mt-8 pt-6 border-t-2 border-dashed border-gray-300 dark:border-gray-600">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                      </svg>
                      Değişiklik Yapılmamış İlk Hali
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      Bu metin üzerinde henüz hiçbir değişiklik yapılmadan önceki orijinal halidir.
                    </p>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border-2 border-gray-300 dark:border-gray-700">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                            {renderTextRuns(originalRuns)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        {/* Sticky Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 flex justify-end px-8 py-4 border-t border-gray-200 dark:border-gray-700 shadow-sm">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnChangeHistoryModal;

